import { CalendarOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import styles from './index.module.less';
import Image from 'next/image';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import useScreenSize from '@/lib/hooks/utils/useScreenSize';
import { useTranslation } from 'next-i18next';
import { InstagramAnonymouslyStoriesModel } from '@/types/anonymously.interface';
import { useRouter } from 'next/router';
import { objToQueryString } from '@/shared/utils';
import { UserInfo } from '@/types/home';
import { getUserInfo } from '@/lib/service';
import { useUserProvider } from '@/context/UserProvider';
import { Select } from 'antd';
import dayjs from 'dayjs';

import dynamic from 'next/dynamic';
import StoriesLinkContent from '../StoriesLinkContent';

// 将 story 数据存储到 localStorage，有效期 1 天
export const saveStoryToLocalStorage = (item: any) => {
  const key = `story_${item.story_id}`;
  const data = {
    ...item,
    timestamp: new Date().getTime(),
  };
  localStorage.setItem(key, JSON.stringify(data));
};

// 检查并清理过期的 story 数据
export const cleanExpiredStories = () => {
  const now = new Date().getTime();
  const oneDayInMs = 24 * 60 * 60 * 1000;

  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith('story_')) {
      try {
        const data = JSON.parse(localStorage.getItem(key));
        if (now - data.timestamp > oneDayInMs) {
          localStorage.removeItem(key);
        }
      } catch (e) {
        localStorage.removeItem(key);
      }
    }
  });
};

const LocalImage = dynamic(() => import('@/components/Common/LocalImage'), {
  ssr: false,
});

const StoriesListContent = ({
  data,
  isReport,
  title,
  media_name,
  avatar,
  media_url,
}: {
  media_name?: string;
  avatar?: string;
  media_url?: string;
  title?: string;
  data?: InstagramAnonymouslyStoriesModel[];
  isReport?: boolean;
}) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [isHorizontallyScrollable, setHorizontallyScrollable] = useState<boolean>(false);
  const [canLeftScrollable, setCanLeftScrollable] = useState<boolean>(false);
  const [canRightScrollable, setCanRightScrollable] = useState<boolean>(false);
  const [currentDate, setCurrentData] = useState('0');
  const { width } = useScreenSize();
  const { t } = useTranslation();
  const [morenum, setMorenum] = useState(8);

  const router = useRouter();
  const [userInfo, setUserInfo] = useState<UserInfo>(null);
  const { isLogin } = useUserProvider();

  const getItemWidth = useMemo(() => {
    if (width > 900) {
      return 284;
    } else {
      return 184;
    }
  }, [width]);

  const getUserInfoAsync = () => {
    if (!isLogin) {
      setUserInfo(null);
      return;
    }

    if (userInfo !== null) return;

    getUserInfo()
      .then((res) => {
        setUserInfo(res?.data);
      })
      .catch((e) => {
        setUserInfo(null);
      });
  };

  useEffect(() => {
    if (width < 915) {
      setMorenum(6);
    }
    if (width > 915) {
      setMorenum(8);
    }
  }, [width]);

  const nowData = useMemo(() => {
    return data
      ?.filter((item) => item?.publish_time?.includes(currentDate === '0' ? '' : currentDate))
      .sort((a, b) => +new Date(b.publish_time) - +new Date(a.publish_time));
  }, [currentDate, data]);

  const scrollToIndex = (isLeft: boolean) => {
    if (divRef.current) {
      var leftOffset = Math.floor(divRef.current.scrollLeft / getItemWidth) * getItemWidth;
      if (isLeft) {
        leftOffset = divRef.current.scrollLeft - getItemWidth <= 0 ? 0 : leftOffset - getItemWidth;
      } else {
        leftOffset =
          (divRef.current.scrollLeft / getItemWidth + 1) * getItemWidth >
          divRef.current.scrollWidth - divRef.current.clientWidth
            ? divRef.current.scrollWidth - divRef.current.clientWidth
            : leftOffset + getItemWidth;
      }
      divRef.current.scrollTo(leftOffset, 0);
    }
  };

  const handleScroll = () => {
    if (divRef.current) {
      const isScrollable = divRef.current.scrollWidth > divRef.current.clientWidth;
      setHorizontallyScrollable(isScrollable);
      setCanLeftScrollable(divRef.current.scrollLeft > 0);
      setCanRightScrollable(divRef.current.scrollLeft + divRef.current.clientWidth < divRef.current.scrollWidth);
    }
  };

  useEffect(() => {
    if (divRef.current) {
      divRef.current.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (divRef.current) {
        divRef.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [data, width]);

  useEffect(() => {
    setCurrentData(() => '0');

    handleScroll();
    const handleResize = () => {
      handleScroll();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [data]);

  // 当前时间线内有stories的时间范围
  const dateStr = () => {
    const dateArr = data?.map((item) => +new Date(item?.publish_time.split(' ')?.[0]));
    const maxDate = dayjs(Math.max.apply(Math, dateArr)).format('MM/DD');
    const minDate = dayjs(Math.min.apply(Math, dateArr)).format('MM/DD');
    return minDate + ' ~ ' + maxDate;
  };

  // 选择日期options
  const selectOptions = useCallback(() => {
    const dates = data?.map((item) => item?.publish_time.split(' ')[0]) || [];
    const uniqueDates = [...new Set(dates)];
    return [
      { label: dateStr(), value: '0' },
      ...uniqueDates?.map((item) => ({
        label: dayjs(item).format('MM-DD'),
        value: dayjs(item).format('MM-DD'),
      })),
    ];
  }, [data]);

  const getEmptyContent = useMemo(() => {
    getUserInfoAsync();

    return (
      <div style={{ display: 'grid', placeItems: 'center', marginBottom: '35px' }}>
        <Image src={'/images/freetools/story_empty_icon.webp'} alt={''} width={80} height={80} />
        <p style={{ marginTop: '20px' }}>{t('No Stories in the last 24 hours.')}</p>
        <p>
          {t(
            'Don’t worry, Dolphin Radar will continue monitoring, and you can check back later or tomorrow for any updates!'
          )}
        </p>
      </div>
    );
  }, [isLogin, userInfo?.email]);

  return (
    <>
      <div className={styles.groundContent}>
        <div className={styles.title}>
          <div className={styles.titleLeft}>
            <span>{nowData?.length ?? 0}</span>
            <span>{title || t('Stories')} </span>
            {!title && (
              <Image src={'/images/report/like-star.webp'} alt={'star'} loading="lazy" width={32} height={32} />
            )}
          </div>
          {isReport && !!data?.length && (
            <Select
              value={currentDate}
              options={selectOptions()}
              onSelect={(v) => {
                setCurrentData(() => v);
              }}
              suffixIcon={<CalendarOutlined />}
              style={{ minWidth: '120px' }}
            />
          )}
        </div>

        {data?.length > 0 ? (
          <div className={styles.bottomContent}>
            {isHorizontallyScrollable && (
              <LeftOutlined
                onClick={() => scrollToIndex(true)}
                className={classNames(styles.leftOutlined, canLeftScrollable ? 'iconAble' : 'iconDisable')}
              />
            )}

            {!isReport && (
              <div className={styles.backContent} ref={divRef}>
                {(data ?? [])?.map((item, index: number) => {
                  return (
                    <div
                      key={item?.cover_image + index}
                      className={styles.desc}
                      onClick={() => {
                        cleanExpiredStories();
                        saveStoryToLocalStorage(item);

                        setTimeout(() => {
                          const newWindow = window.open(
                            `/anonymous-instagram-story-viewer/story?${objToQueryString({
                              ...item,
                              media_name: media_name,
                            })}`,
                            '_blank'
                          );
                          return newWindow;
                        }, 600);
                      }}
                    >
                      <LocalImage
                        src={item?.cover_image}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          overflow: 'hidden',
                        }}
                      />
                      <div className={styles.referContainer}>
                        <StoriesLinkContent links={item?.link_urls} referToList={item?.mention_names} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {isReport && (
              <div className={styles.isReeportList}>
                {(nowData?.length > morenum ? nowData?.slice(0, morenum) : (nowData ?? []))?.map((item) => {
                  return (
                    <div
                      key={item?.story_id}
                      className={styles.desc}
                      onClick={() => {
                        cleanExpiredStories();
                        saveStoryToLocalStorage(item);

                        setTimeout(() => {
                          const newWindow = window.open(
                            `/anonymous-instagram-story-viewer/story?${objToQueryString({
                              ...item,
                              media_name: media_name,
                            })}`,
                            '_blank'
                          );
                          return newWindow;
                        }, 600);
                      }}
                    >
                      <LocalImage
                        src={item?.cover_image}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          overflow: 'hidden',
                        }}
                      />
                      <span
                        style={{ position: 'absolute', bottom: '15px', left: '15px', color: '#FFF', fontSize: '14px' }}
                      >
                        {item?.publish_time}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {isHorizontallyScrollable && (
              <RightOutlined
                onClick={() => scrollToIndex(false)}
                className={classNames(styles.rightOutlined, canRightScrollable ? 'iconAble' : 'iconDisable')}
              />
            )}
          </div>
        ) : (
          <>{!isReport && getEmptyContent}</>
        )}

        {nowData?.length > morenum && nowData?.length !== morenum - 1 && isReport && (
          <div className={styles.more}>
            <div className={styles.btn} onClick={() => setMorenum(nowData?.length + 1)}>
              {t('more')}
            </div>
          </div>
        )}
        {!nowData?.length && isReport && (
          <div className={styles.empyt}>{t('Keep an eye out for upcoming Stories and stay tuned for updates!')}</div>
        )}

        {media_name && !isReport && !data?.length && (
          <div>
            <p style={{ color: 'var(--app-text-30-color)' }}>
              {t(
                'You can only see Stories from the last 24 hours. Add this account to your tracking list to save all Stories from when tracking begins.'
              )}
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default StoriesListContent;
